import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { mdiMagnify } from '@mdi/js';
import Icon from '@mdi/react';
import './StockCheck.css';
import { getApiUrl } from '../config/api';
import { useQuery } from '@tanstack/react-query';

import ProductGrid from '../BodyComponent/ProductGrid';
import Pagination from '../BodyComponent/Pagination';
import ExplanationProduct from '../BodyComponent/ExplanationProduct';
import CustomModal from '../BodyComponent/CustomModal';
import ProductGridSkeleton from '../BodyComponent/ProductGridSkeleton';

// 需求澄清：本元件需支援商品查詢、分頁、圖片放大與加入購物車功能，並已經採用 @CustomModal.js 來顯示商品圖片與購物車操作。

function StockCheck() {
    const [searchParams, setSearchParams] = useSearchParams();

    // 狀態管理（只保留 UI 互動相關）
    const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
    const [itemsPerPage, setItemsPerPage] = useState(25); // 桌面預設 25，手機 10
    const [inputValue, setInputValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');
    const [selectedBarcode, setSelectedBarcode] = useState('');

    // 響應式每頁顯示數量
    const updateItemsPerPage = useCallback(() => {
        const width = window.innerWidth;
        setItemsPerPage(width <= 768 ? 10 : 25);
    }, []);

    // 監聽 URL 參數變化
    useEffect(() => {
        const page = searchParams.get('page');
        if (page) {
            setCurrentPage(parseInt(page));
        } else {
            setCurrentPage(1);
        }
    }, [searchParams]);

    // 初始化與監聽視窗尺寸變化
    useEffect(() => {
        updateItemsPerPage();
        window.addEventListener('resize', updateItemsPerPage);
        // 檢查是否需要重置頁碼
        const shouldReset = sessionStorage.getItem('resetStockPage') === 'true';
        if (shouldReset) {
            setCurrentPage(1);
            sessionStorage.removeItem('resetStockPage');
        }
        return () => window.removeEventListener('resize', updateItemsPerPage);
    }, [updateItemsPerPage]);

    // 監聽 currentPage 或 searchTerm 變化時自動滾動到最上方
    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, [currentPage, searchTerm]);

    // 用 useQuery 快取 API 請求，使用智能緩存策略
    const { data, isLoading, error } = useQuery({
        queryKey: ['products', searchTerm, currentPage, itemsPerPage],
        queryFn: async () => {
            const apiUrl = getApiUrl('/orders/get_product_visible_quantity');
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                },
                body: JSON.stringify({
                    search: searchTerm || '',
                    page: currentPage,
                    limit: itemsPerPage
                })
            });
            const data = await response.json();
            return {
                products: data.data.map(item => ({
                    id: item.id,
                    barcode: item.條碼,
                    product_name: item.品名,
                    remaining_stock: item.剩餘庫存,
                    visible_quantity: item.現貨系統可販售數,
                    system_price: item.現貨系統價格,
                    system_date: item.現貨系統活動,
                    producer: item.出品商,
                    series: item.系列,
                    product_type: item.類型,
                    role1: item.角色1,
                    role2: item.角色2,
                    role3: item.角色3,
                    features: item.其他,
                    specification: item.規格,
                    imageUrl: `https://cdn.jsdelivr.net/gh/WaynezProg/product_picture/all/${item.條碼}.webp`,
                    productUrl: `https://chiikawamarket.jp/products/${item.條碼}`
                })),
                totalPages: data.total_pages || 0
            };
        },
        keepPreviousData: true,
        staleTime: 30 * 60 * 1000
    });

    // 搜尋框輸入
    const handleSearchChange = (event) => {
        setInputValue(event.target.value);
    };

    // 按下搜尋按鈕
    const handleSearchClick = () => {
        setSearchTerm(inputValue);
        setCurrentPage(1);
    };

    // 按下 Enter 觸發查詢，並跳到第一頁
    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            setSearchTerm(inputValue);
            setCurrentPage(1);
        }
    };

    // handlePageChange 只需 setCurrentPage 與 setSearchParams
    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= (data?.totalPages || 0)) {
            setCurrentPage(newPage);
            setSearchParams({ page: newPage.toString() });
            // 不需要再 window.scrollTo，交給 useEffect 處理
        }
    };

    // Modal 控制
    const openModal = (imageUrl, barcode) => {
        const img = new window.Image();
        img.src = imageUrl;
        img.onload = () => {
            setSelectedImage(imageUrl);
            setSelectedBarcode(barcode || '');
            setIsModalOpen(true);
        };
        img.onerror = () => {
            setSelectedImage('/404.jpg');
            setSelectedBarcode(barcode || '');
            setIsModalOpen(true);
        };
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedImage('');
        setSelectedBarcode('');
    };

    const handleOpenModal = (imageUrl, barcode) => {
        openModal(imageUrl, barcode);
    };

    // 取用快取資料
    const productData = data?.products || [];
    const totalPages = data?.totalPages || 0;

    // UI 組件
    return (
        <div>
            <ExplanationProduct />
            <div className="search-container">
                <input
                    type="text"
                    value={inputValue}
                    onChange={handleSearchChange}
                    onKeyPress={handleKeyPress}
                    placeholder="搜尋商品..."
                    className="search-input"
                />
                <button className="search-button" onClick={handleSearchClick}>
                    <Icon path={mdiMagnify} size={1} color="white" />
                </button>
            </div>
            {isLoading ? (
                <ProductGridSkeleton count={itemsPerPage} />
            ) : error ? (
                <div>資料載入失敗，請稍後再試。</div>
            ) : (
                <div>
                    <ProductGrid 
                        productData={productData} 
                        openModal={handleOpenModal}
                    />
                    <Pagination 
                        currentPage={currentPage} 
                        totalPages={totalPages} 
                        onPageChange={handlePageChange} 
                    />
                </div>
            )}
            <CustomModal
                isOpen={isModalOpen}
                onClose={closeModal}
                imageSrc={selectedImage}
                barcode={selectedBarcode}
            />
        </div>
    );
}

export default StockCheck;
